import { useCallback, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { FormattedMessage, useIntl } from 'react-intl';

import { highlightStyle, ScrollContainer } from '@/components/atom/QuestionsListItems/QuestionsListItems.styles';
import useRovingTabIndex from '@/hooks/useRovingTabIndex';
import { EFaqsCategoryIcons } from '@/store/faq/config';
import { TFAQsItem } from '@/store/faq/types';

type QuestionsListItemsProps = {
  isOnboarding: boolean;
  faqsList: TFAQsItem[];
  searchValue: string;
  handleSelection: (faq: TFAQsItem) => void;
};

const LISTBOX_LABEL = 'chatPage.servicesSection.exemplary_questions.listboxLabel';

const QuestionsListItems = ({ isOnboarding, faqsList, searchValue, handleSelection }: QuestionsListItemsProps) => {
  const intl = useIntl();

  const onboardingQuestion: TFAQsItem = useMemo(
    () => ({
      id: 'onboardingQuestion',
      name: intl.formatMessage({
        id: 'chatPage.servicesSection.exemplary_questions_onboarding_one',
      }),
      icon: EFaqsCategoryIcons.PERFORM,
      type: EFaqsCategoryIcons.PERFORM,
      tooltipText: 'chatPage.chatInput.tooltip.stats',
    }),
    [intl],
  );

  const faqsToRender = useMemo<TFAQsItem[]>(
    () => (isOnboarding ? [onboardingQuestion] : faqsList),
    [isOnboarding, onboardingQuestion, faqsList],
  );

  const handleActivate = useCallback(
    (index: number) => {
      if (faqsToRender[index]) {
        handleSelection(faqsToRender[index]);
      }
    },
    [faqsToRender, handleSelection],
  );

  const { handleKeyDown, getTabIndex, setItemRef } = useRovingTabIndex({
    itemCount: faqsToRender.length,
    orientation: 'vertical',
    onActivate: handleActivate,
  });

  /** Scrolls a focused option into view within the scroll container. */
  const handleFocus = useCallback((el: HTMLElement | null) => {
    el?.scrollIntoView({ block: 'nearest' });
  }, []);

  const listboxLabel = intl.formatMessage({
    id: LISTBOX_LABEL,
    defaultMessage: 'Example questions',
  });

  if (faqsToRender.length === 0) {
    return (
      <ScrollContainer
        role="listbox"
        aria-label={listboxLabel}
        tabIndex={0}
        data-testid="example-questions-list"
        className="list-group border rounded margin-bottom-20"
      >
        <div className="list-group-item list-group-item-link">
          <FormattedMessage id="chatPage.servicesSection.exemplary_questions.noExampleQuestions" />
        </div>
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer
      role="listbox"
      aria-label={listboxLabel}
      data-testid="example-questions-list"
      className="list-group border rounded margin-bottom-20"
      onKeyDown={handleKeyDown}
    >
      {faqsToRender.map((faq, index) => (
        <button
          key={`filter-${faq.id}`}
          role="option"
          tabIndex={getTabIndex(index)}
          ref={setItemRef(index)}
          className={`list-group-item list-group-item-link ${
            faq.id === 'onboardingQuestion' ? 'list-group-item-primary bg-highlight-lightest' : ''
          }`}
          onClick={() => handleSelection(faq)}
          onFocus={(e) => handleFocus(e.currentTarget)}
        >
          <Highlighter
            highlightStyle={highlightStyle}
            searchWords={[searchValue]}
            autoEscape
            textToHighlight={faq.name}
          />
        </button>
      ))}
    </ScrollContainer>
  );
};

export default QuestionsListItems;
